import { useCallback } from "react";
import { toast } from "sonner";

export type NotificationType = "success" | "error" | "warning" | "info";

interface NotificationOptions {
	title?: string;
	message: string;
	type: NotificationType;
	duration?: number;
	autoClose?: boolean;
}

/**
 * Hook per gestire notifiche toast.
 * Usa sonner sotto il hood.
 *
 * @example
 * const { notify, success, error, warning, info } = useNotification();
 *
 * // Metodo generico
 * notify({
 *   type: 'success',
 *   title: 'Successo',
 *   message: 'Operazione completata',
 *   duration: 3000
 * });
 *
 * // Metodi shortcut
 * success('Prodotto eliminato!');
 * error('Errore durante il caricamento');
 * warning('Questa azione è irreversibile');
 * info('Informazione importante');
 */
export const useNotification = () => {
	const notify = useCallback((options: NotificationOptions) => {
		const { title, message, type, duration = 4000, autoClose = true } = options;

		const toastOptions = {
			description: message,
			duration: autoClose ? duration : Infinity,
		};

		switch (type) {
			case "success":
				toast.success(title || getDefaultTitle(type), toastOptions);
				break;
			case "error":
				toast.error(title || getDefaultTitle(type), toastOptions);
				break;
			case "warning":
				toast.warning(title || getDefaultTitle(type), toastOptions);
				break;
			case "info":
				toast.info(title || getDefaultTitle(type), toastOptions);
				break;
		}
	}, []);

	const success = useCallback(
		(message: string, title?: string) => {
			notify({ type: "success", message, title });
		},
		[notify],
	);

	const error = useCallback(
		(message: string, title?: string) => {
			notify({ type: "error", message, title });
		},
		[notify],
	);

	const warning = useCallback(
		(message: string, title?: string) => {
			notify({ type: "warning", message, title });
		},
		[notify],
	);

	const info = useCallback(
		(message: string, title?: string) => {
			notify({ type: "info", message, title });
		},
		[notify],
	);

	return {
		notify,
		success,
		error,
		warning,
		info,
	};
};

function getDefaultTitle(type: NotificationType): string {
	switch (type) {
		case "success":
			return "Successo";
		case "error":
			return "Errore";
		case "warning":
			return "Attenzione";
		case "info":
			return "Informazione";
		default:
			return "";
	}
}
